import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  AddToWishlistApi,
  ClearWishlistApi,
  DeleteWishlistItemApi,
  GetWishlistApi,
} from "../../api/product-processing-api/WishlistApi";

const STORAGE_KEY = "agavian_wishlist_items";

const isBrowser = () => typeof window !== "undefined";

const readStoredWishlist = () => {
  if (!isBrowser()) return [];

  try {
    const storedValue = window.localStorage.getItem(STORAGE_KEY);
    if (!storedValue) return [];

    const parsedValue = JSON.parse(storedValue);
    if (Array.isArray(parsedValue)) return parsedValue;
    if (parsedValue && typeof parsedValue === "object") {
      return Object.values(parsedValue);
    }
  } catch (error) {
    console.error("Unable to read wishlist", error);
  }

  return [];
};

const writeStoredWishlist = (itemsById) => {
  if (!isBrowser()) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.values(itemsById)));
  } catch (error) {
    console.error("Unable to save wishlist", error);
  }
};

const resolveProductId = (product = {}) =>
  product?.productId ||
  product?.product?.productId ||
  product?.productDTO?.productId ||
  product?.product?.id ||
  product?.productDTO?.id ||
  product?.id ||
  null;

const resolveWishlistItemId = (item = {}) =>
  item?.wishlistItemId || item?.id || item?.wishlistId || item?.wishlist?.wishlistItemId || null;

const normalizeWishlistItem = (product = {}) => {
  const nestedProduct = product?.product || product?.productDTO || {};
  const productId = resolveProductId(product);

  if (!productId) return null;

  const selectedImage =
    product?.selectedImageThumbKey ||
    product?.imageThumbNail ||
    product?.imageThumbnail ||
    product?.imageUrl ||
    nestedProduct?.selectedImageThumbKey ||
    nestedProduct?.imageThumbNail ||
    nestedProduct?.imageThumbnail ||
    nestedProduct?.imageUrl ||
    "";

  return {
    wishlistItemId: resolveWishlistItemId(product),
    productId,
    name: product?.name || nestedProduct?.name || "Product",
    description: product?.description || nestedProduct?.description || "",
    categoryName: product?.categoryName || nestedProduct?.categoryName || "",
    price:
      product?.price ||
      product?.sellingPrice ||
      product?.distributionPrice ||
      nestedProduct?.price ||
      nestedProduct?.sellingPrice ||
      nestedProduct?.distributionPrice ||
      "",
    originalPrice: product?.originalPrice || product?.mrp || nestedProduct?.originalPrice || "",
    discount: product?.discount || product?.discountPercent || product?.discountPercentage || "",
    imageThumbNail: selectedImage,
    averageRating: product?.averageRating || nestedProduct?.averageRating || "",
    availableStock:
      product?.availableStock ?? product?.stockQuantity ?? nestedProduct?.availableStock ?? null,
    magicEnabled: Boolean(product?.magicEnabled || nestedProduct?.magicEnabled),
    colorEnabled: Boolean(product?.colorEnabled || nestedProduct?.colorEnabled),
    savedAt: product?.savedAt || product?.createdAt || new Date().toISOString(),
  };
};

const buildItemsById = (items = []) =>
  items.reduce((acc, item) => {
    const normalizedItem = normalizeWishlistItem(item);
    if (normalizedItem?.productId) {
      acc[String(normalizedItem.productId)] = normalizedItem;
    }
    return acc;
  }, {});

const extractWishlistItems = (payload) => {
  const response = payload?.response ?? payload?.data?.response ?? payload?.data ?? payload;

  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.content)) return response.content;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.wishlistItems)) return response.wishlistItems;
  if (Array.isArray(response?.wishlistItemDTOS)) return response.wishlistItemDTOS;
  if (Array.isArray(response?.wishlistItemDTOs)) return response.wishlistItemDTOs;
  if (Array.isArray(response?.list)) return response.list;
  if (Array.isArray(response?.records)) return response.records;
  if (response && typeof response === "object" && resolveProductId(response)) return [response];

  return [];
};

const buildAddWishlistPayload = (product = {}) => ({
  productId: resolveProductId(product),
  colorId: product?.colorId || product?.selectedColorId || 0,
  sizeId: product?.sizeId || product?.selectedSizeId || 0,
  noteAnswer: product?.noteAnswer || "",
});

const findWishlistItemForProduct = (items = [], product) => {
  const productId = resolveProductId(product);
  return items.find((item) => String(resolveProductId(item)) === String(productId)) || items[0];
};

export const hydrateWishlist = createAsyncThunk(
  "wishlist/hydrateWishlist",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await GetWishlistApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.response?.data?.message || err?.message,
      });
    }
  },
  {
    condition: (payload, { getState }) => {
      const wishlist = getState()?.wishlist || {};
      return !wishlist.hydrated && !wishlist.loading;
    },
  },
);

export const addToWishlist = createAsyncThunk(
  "wishlist/addToWishlist",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await AddToWishlistApi(buildAddWishlistPayload(payload));
      return {
        apiResponse: response?.data,
        product: payload,
      };
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.response?.data?.message || err?.message,
      });
    }
  },
);

export const removeFromWishlist = createAsyncThunk(
  "wishlist/removeFromWishlist",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const wishlistState = getState()?.wishlist || {};
      const productId = typeof payload === "object" ? resolveProductId(payload) : payload;
      const existingItem =
        wishlistState.itemsById?.[String(productId)] ||
        (typeof payload === "object" ? payload : null);
      const wishlistItemId = resolveWishlistItemId(existingItem);

      if (wishlistItemId) {
        await DeleteWishlistItemApi(wishlistItemId);
      }

      return { productId, wishlistItemId };
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.response?.data?.message || err?.message,
      });
    }
  },
);

export const toggleWishlistProduct = createAsyncThunk(
  "wishlist/toggleWishlistProduct",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const productId = resolveProductId(payload);
      const existingItem = getState()?.wishlist?.itemsById?.[String(productId)];

      if (existingItem) {
        const wishlistItemId = resolveWishlistItemId(existingItem);
        if (wishlistItemId) {
          await DeleteWishlistItemApi(wishlistItemId);
        }

        return {
          action: "remove",
          productId,
          wishlistItemId,
        };
      }

      const response = await AddToWishlistApi(buildAddWishlistPayload(payload));
      return {
        action: "add",
        apiResponse: response?.data,
        product: payload,
      };
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.response?.data?.message || err?.message,
      });
    }
  },
);

export const clearWishlist = createAsyncThunk(
  "wishlist/clearWishlist",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await ClearWishlistApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue({
        errorMessage: err?.response?.data?.message || err?.message,
      });
    }
  },
);

const initialState = {
  hydrated: false,
  loading: false,
  posting: false,
  deleting: false,
  clearing: false,
  errorMessage: "",
  itemsById: {},
};

const WishlistSlice = createSlice({
  name: "wishlist",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(hydrateWishlist.pending, (state) => {
        state.loading = true;
        state.errorMessage = "";
      })
      .addCase(hydrateWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.hydrated = true;
        state.itemsById = buildItemsById(extractWishlistItems(action.payload));
        writeStoredWishlist(state.itemsById);
      })
      .addCase(hydrateWishlist.rejected, (state, action) => {
        state.loading = false;
        state.hydrated = true;
        state.errorMessage = action.payload?.errorMessage || "";
        state.itemsById = buildItemsById(readStoredWishlist());
      });

    builder
      .addCase(addToWishlist.pending, (state) => {
        state.posting = true;
        state.errorMessage = "";
      })
      .addCase(addToWishlist.fulfilled, (state, action) => {
        state.posting = false;
        const apiItems = extractWishlistItems(action.payload?.apiResponse);
        const wishlistItem = normalizeWishlistItem(
          findWishlistItemForProduct(apiItems, action.payload?.product) || action.payload?.product,
        );

        if (wishlistItem?.productId) {
          state.itemsById[String(wishlistItem.productId)] = wishlistItem;
          state.hydrated = true;
          writeStoredWishlist(state.itemsById);
        }
      })
      .addCase(addToWishlist.rejected, (state, action) => {
        state.posting = false;
        state.errorMessage = action.payload?.errorMessage || "";
      });

    builder
      .addCase(removeFromWishlist.pending, (state) => {
        state.deleting = true;
        state.errorMessage = "";
      })
      .addCase(removeFromWishlist.fulfilled, (state, action) => {
        state.deleting = false;
        const productId = action.payload?.productId;

        if (productId) {
          delete state.itemsById[String(productId)];
          state.hydrated = true;
          writeStoredWishlist(state.itemsById);
        }
      })
      .addCase(removeFromWishlist.rejected, (state, action) => {
        state.deleting = false;
        state.errorMessage = action.payload?.errorMessage || "";
      });

    builder
      .addCase(toggleWishlistProduct.pending, (state) => {
        state.posting = true;
        state.errorMessage = "";
      })
      .addCase(toggleWishlistProduct.fulfilled, (state, action) => {
        state.posting = false;

        if (action.payload?.action === "remove") {
          delete state.itemsById[String(action.payload.productId)];
        } else {
          const apiItems = extractWishlistItems(action.payload?.apiResponse);
          const wishlistItem = normalizeWishlistItem(
            findWishlistItemForProduct(apiItems, action.payload?.product) ||
              action.payload?.product,
          );

          if (wishlistItem?.productId) {
            state.itemsById[String(wishlistItem.productId)] = wishlistItem;
          }
        }

        state.hydrated = true;
        writeStoredWishlist(state.itemsById);
      })
      .addCase(toggleWishlistProduct.rejected, (state, action) => {
        state.posting = false;
        state.errorMessage = action.payload?.errorMessage || "";
      });

    builder
      .addCase(clearWishlist.pending, (state) => {
        state.clearing = true;
        state.errorMessage = "";
      })
      .addCase(clearWishlist.fulfilled, (state) => {
        state.clearing = false;
        state.hydrated = true;
        state.itemsById = {};
        writeStoredWishlist(state.itemsById);
      })
      .addCase(clearWishlist.rejected, (state, action) => {
        state.clearing = false;
        state.errorMessage = action.payload?.errorMessage || "";
      });
  },
});

export default WishlistSlice.reducer;
